import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody, ModalFooter, Button, Alert, Table, Badge, Card, CardBody, Progress } from "reactstrap";
import { createSelector } from "reselect";
import {
  removeImportInvoice, applyImportInvoiceEdit, clearImportPreview,
  toggleImportInvoiceSelected, setAllImportInvoicesSelected,
} from "../../../../../slices/vendorInvoices/reducer";
import { postVendorsInvoices } from "../../../../../slices/vendorInvoices/thunk";
import { getPhases } from "../../../../../slices/phases/thunk";
import { vendorInvoiceAPI } from "../../../../../helpers/api/vendorInvoice";
import ImportStats from "./ImportStats";
import ImportRow from "./ImportRow";
import EditInvoiceModal from "./EditInvoiceModal";

const selectImportState = createSelector(
  (state) => state.VendorInvoices || {},
  (s) => ({ invoices: s.importPreview.invoices || [], stats: s.importPreview.stats || {}, loading: s.loading || false })
);

const ImportModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { invoices, stats, loading } = useSelector(selectImportState);
  const { phases } = useSelector((state) => state.Phases || {});

  const [categories,    setCategories]    = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [editIndex,     setEditIndex]     = useState(null);
  const [error,         setError]         = useState(null);

  const loadCategories    = useCallback(async () => { try { setCategories(await vendorInvoiceAPI.getAllCategories()); } catch {} }, []);
  const loadSubCategories = useCallback(async () => { try { const r = await vendorInvoiceAPI.getAllSubCategories(); setSubCategories(Array.isArray(r) ? r : r.results || []); } catch {} }, []);

  useEffect(() => {
    if (open) {
      loadCategories(); loadSubCategories(); dispatch(getPhases());
    } else {
      setError(null); setEditIndex(null);
      dispatch(clearImportPreview());
    }
  }, [open, dispatch, loadCategories, loadSubCategories]);

  const handleSaveEdit = (payload) => {
    dispatch(applyImportInvoiceEdit(payload));
    setEditIndex(null);
  };

  const handleConfirmImport = async () => {
    setError(null);
    const ready = invoices.filter((inv) => inv.ready_to_save && inv.selected);
    if (ready.length === 0) { setError("No invoices are selected for import"); return; }
    try {
      await dispatch(postVendorsInvoices(ready)).unwrap();
      onSuccess?.(); onClose();
    } catch (err) { setError(err.message || "Failed to import invoices"); }
  };

  return (
    <>
      <EditInvoiceModal
        open={editIndex !== null}
        invoice={editIndex !== null ? invoices[editIndex] : null}
        index={editIndex}
        categories={categories}
        subCategories={subCategories}
        phases={phases}
        reloadCategories={loadCategories}
        onClose={() => setEditIndex(null)}
        onSave={handleSaveEdit}
      />

      <Modal isOpen={open} toggle={onClose} size="xl" scrollable className="modal-fullscreen-lg-down" style={{ maxWidth: "95vw", width: "75vw" }}>
        <div className="modal-header border-0 px-4 py-3" style={{ background: "linear-gradient(135deg, #1a2942 0%, #2c3e5d 100%)", color: "#fff" }}>
          <div className="d-flex align-items-center flex-grow-1">
            <div className="d-flex align-items-center justify-content-center me-3" style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(255,255,255,0.12)" }}>
              <i className="ri-file-download-line fs-4" />
            </div>
            <div>
              <h5 className="mb-0 fw-semibold text-white">Import Vendor Invoices</h5>
              <small className="text-white-50">Review, validate and import invoices from e-Factura</small>
            </div>
          </div>
          <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
        </div>

        <ModalBody className="bg-light p-3" style={{ maxHeight: "75vh" }}>
          <ImportStats stats={stats} />

          {stats.parsing_errors?.length > 0 && (
            <Alert color="danger" className="mb-3 border-start border-danger border-4">
              <h6 className="alert-heading d-flex align-items-center mb-2"><i className="ri-error-warning-line me-2" />{stats.parsing_errors.length} row(s) failed to parse</h6>
              {stats.parsing_errors.map((err, i) => <div key={i} className="small mb-1"><strong>Row {err.row}:</strong> {err.error}</div>)}
            </Alert>
          )}

          <Card className="border-0 shadow-sm">
            <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between" style={{ background: "#f8f9fc" }}>
              <div className="d-flex align-items-center">
                <i className="ri-table-line text-muted me-2" />
                <span className="fw-semibold small text-uppercase text-muted" style={{ letterSpacing: "0.5px" }}>Invoice Details</span>
                <Badge color="light" className="ms-2 text-dark fw-normal border">{invoices.length} record{invoices.length !== 1 ? "s" : ""}</Badge>
              </div>
              <small className="text-muted"><i className="ri-information-line me-1" />Use the edit button to resolve issues</small>
            </div>
            <CardBody className="p-0">
              <div className="table-responsive" style={{ maxHeight: "50vh", overflow: "auto" }}>
                <Table className="table table-sm table-hover align-middle mb-0">
                  <thead className="sticky-top" style={{ top: 0, zIndex: 10, background: "#f8f9fc" }}>
                    <tr>
                      <th className="text-center px-2">
                        <input type="checkbox" className="form-check-input" title="Select all ready invoices"
                          checked={stats.ready_to_save > 0 && stats.selected_ready === stats.ready_to_save}
                          onChange={(e) => dispatch(setAllImportInvoicesSelected(e.target.checked))} />
                      </th>
                      {["Status", "Document", "CIF", "Vendor", "Issue Date", "Due Date", "Total", "Invoice Status", "Phase", "Category", "Sub-category", "Block", "Actions"].map((h, i, arr) => (
                        <th key={i} className={`text-uppercase text-muted fw-semibold ${i === 0 ? "text-center px-3" : i === arr.length - 1 ? "text-end px-3" : "px-2"}`} style={{ fontSize: 11, letterSpacing: "0.5px" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice, i) => (
                      <ImportRow key={i} invoice={invoice} index={i}
                        onEdit={(idx) => setEditIndex(idx)}
                        onDelete={(idx) => dispatch(removeImportInvoice(idx))}
                        onToggle={(idx) => dispatch(toggleImportInvoiceSelected(idx))} />
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {error && <Alert color="danger" className="mt-3 border-start border-danger border-4"><i className="ri-error-warning-line me-2" />{error}</Alert>}
          {loading && <div className="mt-3"><div className="d-flex align-items-center mb-2"><strong className="me-2">Processing...</strong><div className="spinner-border spinner-border-sm text-primary" role="status" /></div><Progress animated color="primary" value={100} /></div>}
        </ModalBody>

        <ModalFooter className="bg-white border-top px-4 py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center text-muted small">
            <i className="ri-shield-check-line me-2 fs-5 text-success" />
            <div>
              <div className="fw-semibold text-dark">{stats.selected_ready || 0} selected · {stats.ready_to_save || 0} of {stats.total || 0} invoices ready</div>
              {stats.validation_issues > 0 && <small>{stats.validation_issues} invoice{stats.validation_issues !== 1 ? "s" : ""} need attention</small>}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button color="light" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button color="success" onClick={handleConfirmImport} disabled={!stats.selected_ready || loading} className="px-4">
              <i className="ri-download-2-line me-1" />Import {stats.selected_ready || 0} Invoice{stats.selected_ready !== 1 ? "s" : ""}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ImportModal;
